import React from "react";
import { Link } from "react-router-dom";
import { PageHero, SectionHeader, SiteLayout } from "./components/SiteLayout";
import { careerBenefits, careerProgram, openRoles, siteData } from "./siteData";

export default function CareersPage() {
  return (
    <SiteLayout title="Careers" description="Career opportunities and mentorship pathways at SaskTech Lab.">
      <PageHero
        eyebrow="Join Our Team"
        title="Build your career through real-world projects and expert mentorship."
        description="At SaskTech Lab, we're not just offering jobs – we're providing a unique career development opportunity. Our model is designed to bridge the gap between education and industry by giving recent graduates and early-career professionals the chance to work on real projects under the guidance of experienced mentors."
        actions={[
          <Link key="contact" to="/contact" className="button-tertiary">
            Reach out about roles
          </Link>,
        ]}
      />

      {/* Why Join */}
      <section className="site-shell section-space">
        <SectionHeader eyebrow="Why Work With Us" title="A career development opportunity unlike any other." description="Real work, expert mentorship, cross-functional exposure, and a portfolio of meaningful projects." />
        <div className="mt-10 grid gap-6 md:grid-cols-2 xl:grid-cols-4">
          {careerBenefits.map((benefit) => (
            <article key={benefit.title} className="panel-card h-full">
              <h3 className="font-display text-2xl font-bold text-emerald-950">{benefit.title}</h3>
              <p className="mt-4 text-sm leading-7 text-slate-600">{benefit.description}</p>
            </article>
          ))}
        </div>
      </section>

      {/* Development Program */}
      <section className="site-shell section-space">
        <SectionHeader eyebrow="Our Development Program" title="A clear path from graduate to leader." description="Candidates should be able to understand how growth happens, not just see a generic hiring page." />
        <div className="mt-10 grid gap-6 md:grid-cols-2 xl:grid-cols-4">
          {careerProgram.map((stage) => (
            <article key={stage.stage} className="panel-card h-full">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-700">{stage.duration}</p>
              <h3 className="mt-2 font-display text-2xl font-bold text-emerald-950">{stage.stage}</h3>
              <ul className="mt-6 space-y-3 text-sm text-slate-600">
                {stage.points.map((point) => (
                  <li key={point} className="list-bullet-item">{point}</li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </section>

      {/* Open Roles */}
      <section className="site-shell section-space">
        <SectionHeader eyebrow="Current Opportunities" title="Open roles available now." description="We are actively looking for motivated individuals ready to grow through real engineering work." />
        <div className="mt-10 grid gap-6 md:grid-cols-2 xl:grid-cols-4">
          {openRoles.map((role) => (
            <article key={role.title} className="panel-card h-full flex flex-col">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-700">{role.location}</p>
              <h3 className="mt-3 font-display text-2xl font-bold text-emerald-950">{role.title}</h3>
              <p className="mt-2 text-sm font-semibold text-slate-500">{role.type}</p>
              <ul className="mt-6 space-y-3 text-sm text-slate-600 flex-1">
                {role.points.map((point) => (
                  <li key={point} className="list-bullet-item">{point}</li>
                ))}
              </ul>
              <div className="mt-6">
                <a
                  href={`mailto:${siteData.email}?subject=Application: ${encodeURIComponent(role.title)}`}
                  className="button-tertiary w-full text-center block"
                >
                  Apply Now
                </a>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* No Perfect Fit */}
      <section className="site-shell section-space pb-10">
        <div className="rounded-[36px] border border-emerald-900/10 bg-gradient-to-br from-white via-emerald-50 to-emerald-100 p-8 text-center lg:p-10">
          <h2 className="font-display text-3xl font-bold text-emerald-950">Don't See a Perfect Fit?</h2>
          <p className="mt-4 max-w-xl mx-auto text-base text-slate-600">
            We're always looking for talented individuals. Send your resume and let us know how you'd like to contribute.
          </p>
          <div className="mt-8">
            <Link to="/contact" className="button-tertiary">Contact Us</Link>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}